// Utils
import { round } from 'lodash';
import { getX, getY } from '../../../../common/maths/geometry/dimensionUtil';
import * as pointLib from '../../../../common/maths/geometry/point';
import type { Point } from '../../../../common/maths/geometry/pointTypes';
import type { FreehandPoint } from './drawingEditorUtils';

export const getFreehandPointDistSquared = (point1: FreehandPoint | undefined, point2: FreehandPoint | undefined) => {
    if (!point1 || !point2) return 0;

    const xd = point1[0] - point2[0];
    const yd = point1[1] - point2[1];
    return xd * xd + yd * yd;
};

// NOTE: Return undefined here to save on data size, if the stroke doesn't have pressure
export const pointHasPressure = (point: FreehandPoint) => {
    const pressure = point?.[2];
    return Number.isFinite(pressure) && pressure !== 0 ? true : undefined;
};

export const getFreehandPointOffset = (startPoint: FreehandPoint, endPoint: FreehandPoint) => {
    const [startX, startY] = startPoint;
    const [endX, endY] = endPoint;
    return pointLib.getPoint(endX - startX, endY - startY);
};

const compactFreehandPoint = ([x, y, pressure]: [number, number, number | undefined]): FreehandPoint => {
    if (pressure === undefined) return [x, y];
    return [x, y, pressure];
};

export const scaleFreehandPoint =
    (scale: number) =>
    ([x, y, pressure]: FreehandPoint): FreehandPoint =>
        compactFreehandPoint([x * scale, y * scale, pressure]);

export const translateFreehandPoint = (offset: Point) => {
    const xOffset: number = getX(offset);
    const yOffset: number = getY(offset);

    return ([x, y, pressure]: FreehandPoint): FreehandPoint =>
        compactFreehandPoint([x + xOffset, y + yOffset, pressure]);
};

export const roundFreehandPoint = ([x, y, pressure]: FreehandPoint): FreehandPoint =>
    compactFreehandPoint([round(x, 1), round(y, 1), pressure]);
